import React from 'react';
import { Box, Container, Typography, Button, Card, CardContent, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { colors } from '../theme';
import RevealOnScroll from './RevealOnScroll';

/**
 * HEURÍSTICAS DE NIELSEN APLICADAS:
 *
 * #1 - Visibilidade do status do sistema:
 *   - Badge "Vídeo em Construção" informa claramente o status do conteúdo
 *   - Feedback visual com animações ao interagir
 *
 * #2 - Correspondência entre sistema e mundo real:
 *   - Linguagem clara e direta: "Transforme sua gestão"
 *   - Ícones representativos e familiares
 *
 * #3 - Controle e liberdade do usuário:
 *   - Botões de CTA claros com ações específicas
 *   - Link direto para checkout sem cadastro prévio
 *
 * #4 - Consistência e padrões:
 *   - Design alinhado com o restante do site
 *   - Paleta de cores e tipografia consistentes
 *
 * #6 - Reconhecimento ao invés de memorização:
 *   - Benefícios visuais com ícones descritivos
 *   - Informação de preço clara e visível
 *
 * #8 - Design estético e minimalista:
 *   - Informações essenciais destacadas
 *   - Espaçamento adequado, sem poluição visual
 *
 * #10 - Ajuda e documentação:
 *   - Seção de benefícios explica o que o sistema faz
 *   - Vídeo demonstrativo (em construção) para facilitar compreensão
 */

const features = [
  {
    icon: <CalendarMonthIcon sx={{ fontSize: 45 }} />,
    title: 'Agendamento 24/7',
    description: 'Seus clientes agendam a qualquer hora, sem precisar ligar',
  },
  {
    icon: <PeopleIcon sx={{ fontSize: 45 }} />,
    title: 'Gestão de Clientes',
    description: 'Histórico completo, preferências e fidelização automática',
  },
  {
    icon: <AttachMoneyIcon sx={{ fontSize: 45 }} />,
    title: 'Controle Financeiro',
    description: 'Acompanhe faturamento, comissões e fluxo de caixa',
  },
  {
    icon: <PhoneAndroidIcon sx={{ fontSize: 45 }} />,
    title: 'App Mobile',
    description: 'Gerencie sua barbearia de qualquer lugar',
  },
  {
    icon: <AssessmentIcon sx={{ fontSize: 45 }} />,
    title: 'Relatórios Inteligentes',
    description: 'Métricas e insights para crescer seu negócio',
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 45 }} />,
    title: 'Reduz Faltas',
    description: 'Lembretes automáticos diminuem faltas em até 70%',
  },
];

const SchedulingSystemSection: React.FC = () => {
  const handleCTAClick = () => {
    window.open('https://www.checkout.barbabarao.com.br', '_blank');
  };

  return (
    <Box
      id="sistema-agendamento"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: colors.blackMatte,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '400px', md: '700px' },
          height: { xs: '400px', md: '700px' },
          backgroundImage: 'url(/vertical_brand.svg)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.03,
          zIndex: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header da Seção */}
        <RevealOnScroll>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              <CalendarMonthIcon
                sx={{
                  fontSize: { xs: 50, md: 70 },
                  color: colors.agedGold,
                }}
              />
            </Box>

            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: colors.beige,
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Transforme a Gestão da Sua Barbearia
            </Typography>

            <Box
              sx={{
                width: '80px',
                height: '3px',
                backgroundColor: colors.agedGold,
                mx: 'auto',
                mb: 3,
              }}
            />

            <Typography
              variant="h5"
              sx={{
                color: colors.beige,
                maxWidth: '900px',
                mx: 'auto',
                opacity: 0.95,
                fontWeight: 400,
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                lineHeight: 1.6,
              }}
            >
              Sistema completo de agendamento, gestão de clientes e faturamento.{' '}
              <Box
                component="span"
                sx={{
                  color: colors.agedGold,
                  fontWeight: 700,
                }}
              >
                A partir de R$70/mês
              </Box>
              , sem complicação.
            </Typography>
          </Box>
        </RevealOnScroll>

        {/* Vídeo Demonstrativo - Em Construção */}
        <RevealOnScroll delay={100}>
          <Box
            sx={{
              mb: { xs: 6, md: 10 },
              position: 'relative',
            }}
          >
            <Card
              sx={{
                backgroundColor: colors.darkWine,
                border: `3px solid ${colors.agedGold}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  backgroundColor: `${colors.blackMatte}CC`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    width: '100%',
                    px: 2,
                  }}
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 3,
                        py: 1.5,
                        backgroundColor: colors.agedGold,
                        borderRadius: '8px',
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.blackMatte,
                          fontWeight: 700,
                          fontSize: { xs: '0.9rem', md: '1.1rem' },
                        }}
                      >
                        🎬 VÍDEO EM CONSTRUÇÃO
                      </Typography>
                    </Box>
                  </motion.div>

                  <Typography
                    variant="h4"
                    sx={{
                      color: colors.beige,
                      fontWeight: 600,
                      fontSize: { xs: '1.3rem', md: '2rem' },
                      mb: 1,
                    }}
                  >
                    Veja o Sistema em Ação
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.beige,
                      opacity: 0.9,
                      fontSize: { xs: '0.9rem', md: '1.1rem' },
                    }}
                  >
                    Em breve, demonstração completa aqui
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        </RevealOnScroll>

        {/* Grid de Benefícios */}
        <Box sx={{ mb: { xs: 6, md: 10 } }}>
          <RevealOnScroll delay={150}>
            <Typography
              variant="h3"
              sx={{
                color: colors.beige,
                textAlign: 'center',
                mb: { xs: 4, md: 6 },
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}
            >
              Por Que Escolher Nosso Sistema?
            </Typography>
          </RevealOnScroll>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <RevealOnScroll delay={200 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: colors.darkWine,
                      border: `2px solid ${colors.agedGold}`,
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 12px 24px ${colors.agedGold}30`,
                        borderColor: colors.agedGold,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 3, md: 4 },
                        textAlign: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          color: colors.agedGold,
                          mb: 2,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: colors.beige,
                          mb: 1.5,
                          fontWeight: 700,
                          fontSize: { xs: '1.2rem', md: '1.4rem' },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.beige,
                          opacity: 0.9,
                          lineHeight: 1.6,
                          fontSize: { xs: '0.95rem', md: '1rem' },
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </RevealOnScroll>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Final - Persuasivo */}
        <RevealOnScroll delay={300}>
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              backgroundColor: colors.agedGold,
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: `0 8px 32px ${colors.agedGold}40`,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: colors.blackMatte,
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}
            >
              Pronto para Profissionalizar?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.blackMatte,
                mb: 4,
                opacity: 0.9,
                maxWidth: '700px',
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
                lineHeight: 1.7,
              }}
            >
              Mais de <strong>500 barbearias</strong> já economizam tempo e aumentam lucros com nosso sistema.
              <br />
              <Box component="span" sx={{ fontWeight: 700, fontSize: '1.3em' }}>
                Comece hoje por apenas R$70/mês.
              </Box>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleCTAClick}
                aria-label="Quero o sistema agora"
                sx={{
                  px: 5,
                  py: 2.5,
                  fontSize: '1.2rem',
                  backgroundColor: colors.darkWine,
                  color: colors.beige,
                  border: `3px solid ${colors.blackMatte}`,
                  fontWeight: 700,
                  borderRadius: '8px',
                  minWidth: '280px',
                  '&:hover': {
                    backgroundColor: colors.blackMatte,
                    color: colors.beige,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Quero o Sistema Agora
              </Button>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: colors.blackMatte,
                mt: 3,
                opacity: 0.8,
                fontSize: { xs: '0.85rem', md: '0.95rem' },
              }}
            >
              ✓ Sem contrato de fidelidade • ✓ Suporte humanizado • ✓ Cancele quando quiser
            </Typography>
          </Box>
        </RevealOnScroll>
      </Container>
    </Box>
  );
};

export default SchedulingSystemSection;
